<template>
  <b-card>
    <loading
      :active="isLoading"
      loader="bars"
      color="#7367f0"
      :is-full-page="fullPage"
    />
    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Search</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
          />
        </div>
      </b-form-group>
    </div>

    <vue-good-table
      mode="remote"
      :columns="columns"
      :rows="rows"
      style-class="vgt-table striped"
      :total-rows="totalRecords"
      :is-loading.sync="isLoadingTable"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
        skipDiacritics: true,
      }"
      :pagination-options="{
        enabled: true,
        perPage: serverParams.perPage,
        perPageDropdownEnabled: false,
        dropdownAllowAll: false,
      }"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
      @on-search="onSearch"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <span v-if="props.column.field == 'action'">
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item @click.prevent="navigateToEdit(props.row.id)">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteUser(props.row.id)">
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </span>
        <span v-else-if="props.column.field == 'role'">
          <span v-if="props.row.role">{{ props.row.role }}</span>
          <span v-else>-</span>
        </span>
        <span v-else-if="props.column.field == 'departments'">
          <span v-if="props.row.departments">{{ props.row.departments }}</span>
          <span v-else>-</span>
        </span>
        <span v-else-if="props.column.field == 'churches'">
          <span v-if="props.row.churches">{{ props.row.churches }}</span>
          <span v-else>-</span>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BFormGroup, BFormInput, BCard, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import Loading from 'vue-loading-overlay'
import _ from 'lodash'
import CryptoJS from 'crypto-js'
import 'vue-loading-overlay/dist/vue-loading.css'
import { VueGoodTable } from 'vue-good-table'
import { isUserLoggedIn } from '@/auth/utils'

export default {
  components: {
    BFormGroup,
    BFormInput,
    BCard,
    VueGoodTable,
    BDropdown,
    BDropdownItem,
    Loading,
  },
  data() {
    return {
      isLoading: true,
      isLoadingTable: false,
      fullPage: false,
      columns: [
        {
          label: 'NIJ',
          field: 'nij',
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Email',
          field: 'email',
        },
        {
          label: 'Role',
          field: 'role',
        },
        {
          label: 'Ministries',
          field: 'departments',
        },
        {
          label: 'Divisions',
          field: 'divisions',
        },
        {
          label: 'Churches',
          field: 'churches',
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      rows: [],
      totalRecords: 0,
      searchTerm: '',
      serverParams: {
        page: 1,
        perPage: 10,
        searchTerm: '',
      },
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    getUserData() {
      return JSON.parse(
        CryptoJS.AES.decrypt(sessionStorage.getItem('userData'), process.env.VUE_APP_COOKIE_SECRET).toString(CryptoJS.enc.Utf8),
      )
    },
    updateParams(newProps) {
      this.serverParams = Object.assign(this.serverParams, newProps)
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage })
      this.loadItems()
    },
    onPerPageChange(params) {
      this.updateParams({ page: 1, perPage: params.currentPerPage })
      this.loadItems()
    },
    onSearch: _.debounce(function (params) {
      this.updateParams({ page: 1, searchTerm: params.searchTerm || '' })
      this.loadItems()
      return false
    }, 350),
    async loadItems() {
      try {
        this.isLoading = true
        this.isLoadingTable = true

        const userData = this.getUserData()
        const viewerRole = userData?.[0]?.role || ''
        const { page, perPage, searchTerm } = this.serverParams

        const { getPayloadToken } = await import('@/utils/payloadToken')
        const token = await getPayloadToken(this.$http, 'listUsers', {
          q: searchTerm || '',
          page,
          perPage,
          sortBy: 'id',
          sortDesc: false,
          afterId: null,
          viewerRole,
        })

        const resp = await this.$http.get('/api/users', {
          params: {
            q: searchTerm || undefined,
            page,
            perPage,
            sortBy: 'id',
            sortDesc: false,
            viewerRole: viewerRole || undefined,
          },
          headers: { 'X-Payload-Token': token },
        })

        this.rows = resp?.data?.users ?? []
        this.totalRecords = resp?.data?.total ?? 0
      } catch (e) {
        // keep current table state on error
      } finally {
        this.isLoading = false
        this.isLoadingTable = false
      }
    },
    navigateToEdit(id) {
      this.$router.push({ name: 'edit-user', params: { id } })
    },
    deleteUser(id) {
      const isLoggedIn = isUserLoggedIn()
      if (!isLoggedIn) {
        return this.$router.replace('/login')
      }

      if (!this.$can('delete', 'User')) {
        this.$swal({
          icon: 'error',
          title: 'Forbidden!',
          text: 'You are not authorized to do this action.',
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      } else {
        this.$swal({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete it!',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        }).then(async result => {
          if (result.value) {
            this.isLoading = true

            try {
              const signRole = await this.$http.post(`${process.env.VUE_APP_API_BASE_URL}/api/payload/sign`, {
                action: 'deleteUserRole',
                payload: { id },
                resourceId: id,
              })
              await this.$http.delete(`${process.env.VUE_APP_API_BASE_URL}/api/user/role/${id}`, {
                headers: { 'X-Payload-Token': signRole.data.token },
              })
              const signUser = await this.$http.post(`${process.env.VUE_APP_API_BASE_URL}/api/payload/sign`, {
                action: 'deleteUser',
                payload: { id },
                resourceId: id,
              })
              await this.$http.delete(`${process.env.VUE_APP_API_BASE_URL}/api/user/${id}`, {
                headers: { 'X-Payload-Token': signUser.data.token },
              })
              this.isLoading = false
              this.$swal({
                icon: 'success',
                title: 'Deleted!',
                text: 'The user has been deleted.',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              await this.loadItems()
            } catch (err) {
              this.isLoading = false
              this.$swal({
                icon: 'error',
                title: 'Error',
                text: err?.response?.data?.error?.message || 'Failed to delete user',
                customClass: { confirmButton: 'btn btn-success' },
              })
            }
          } else {
            this.isLoading = false
          }
        })
      }
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
table.vgt-table {
  border: none;
  font-size: 1rem;
}
.vgt-table thead th {
  outline: none;
  background-color: #f3f2f7;
}
table.vgt-table td {
  vertical-align: middle !important;
}
.vgt-wrap__footer {
  border: none;
}
</style>
